import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { CardView } from 'design-system/CardView';
import { SettingsItemNavigate } from 'design-system/SettingsItem';
import { AppView } from 'design-system/AppView';
import Button from 'design-system/Button';
import EmojiText from 'design-system/EmojiText';
import StatusBar from 'design-system/StatusBar';
import EmojiPickerModal from 'design-system/emojiPicker/EmojiPickerModal';
import { IconEmma } from 'design-system/icons';
import { rem } from 'design-system/values';
import WebEmojiPickerWeb from 'features/transactions/components/WebEmojiPicker.web';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import { selectShouldShowCsvImports } from 'features/accounts/hooks/selectors';
import { selectShowNewPhysicalAssets } from 'features/accounts/selectors';
import { Sign } from 'utils/types/navigation';
import { isYesterdayOrOlder } from 'utils/date/resetDay';
import { getPropertyName, PHYSICAL_ASSET_TYPES, } from 'features/accounts/constants';
import { isWeb } from '../../../constants';
import { webPickerWidth } from '../components/EmojiSelector';
const AddAnEmojiScreen = ({ navigation, route, }) => {
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Add emoji',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const [emojiSelected, setSelectedEmoji] = useState(route.params.emoji || '');
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const shouldShowCsvImports = useAppSelector(selectShouldShowCsvImports);
    const keyBack = route.params.keyBack || (isWeb ? undefined : route.key);
    const { params } = route;
    const [webEmojiPickerState, setWebEmojiPickerState] = useState(undefined);
    const { sidebarWidth, width } = useAppFrameDimensions(isWeb);
    const emojiViewRef = useRef(null);
    const clearWebEmojiPickerState = useCallback(() => {
        setWebEmojiPickerState(undefined);
    }, []);
    const onEmojiModalClose = useCallback(() => {
        setIsEmojiPickerOpen(false);
    }, []);
    const onEmojiSelected = useCallback((e) => {
        clearWebEmojiPickerState();
        setSelectedEmoji(e);
        onEmojiModalClose();
    }, [clearWebEmojiPickerState, onEmojiModalClose]);
    const onPressEmoji = useCallback(() => {
        setIsEmojiPickerOpen(true);
        if (isWeb) {
            if (webEmojiPickerState) {
                clearWebEmojiPickerState();
                return;
            }
            emojiViewRef.current?.measure((_, __, ___, height, fx, fy) => {
                setWebEmojiPickerState({
                    fx: fx - sidebarWidth + (width - webPickerWidth - rem(36)),
                    fy: fy + height - rem(8),
                });
            });
        }
        else {
            EmojiPickerModal.show(emojiSelected, onEmojiSelected, onEmojiModalClose);
        }
    }, [
        clearWebEmojiPickerState,
        emojiSelected,
        onEmojiModalClose,
        onEmojiSelected,
        sidebarWidth,
        webEmojiPickerState,
        width,
    ]);
    const showNewPhysicalAssets = useAppSelector(selectShowNewPhysicalAssets);
    const onAddValuation = useCallback((routeParams) => {
        navigation.navigateDeprecated('SelectDate', {
            longTitle: `When did you buy this ${getPropertyName(routeParams.type).toLowerCase()}?`,
            buttonText: 'Continue',
            onSubmitDate: (date) => {
                const isPastDate = isYesterdayOrOlder(date);
                const title = isPastDate
                    ? 'What was the initial value?'
                    : 'What is the current value?';
                navigation.navigateDeprecated('AddBalance', {
                    ...routeParams,
                    purchaseDate: date,
                    custom: {
                        title,
                        webSubtitle: title,
                        button: 'Continue',
                        initialSign: Sign.Positive,
                    },
                    ...(isPastDate && {
                        onContinue: (amount, currency) => {
                            navigation.navigateDeprecated('HasValuationChanged', {
                                ...routeParams,
                                purchaseDate: date,
                                initialValue: amount,
                                currency,
                            });
                        },
                    }),
                });
            },
        });
    }, []);
    const saveDetails = useCallback(() => {
        const { type } = route.params;
        clearWebEmojiPickerState();
        const routeParams = {
            ...params,
            emoji: emojiSelected,
            keyBack,
        };
        const isPhysicalAsset = PHYSICAL_ASSET_TYPES.has(type);
        if (route.params.isChangingLogo) {
            navigation.navigateDeprecated('DummyAccountCreated', {
                ...routeParams,
            });
        }
        else if (isPhysicalAsset && showNewPhysicalAssets) {
            onAddValuation(routeParams);
        }
        else if (shouldShowCsvImports && !isPhysicalAsset) {
            navigation.navigateDeprecated('DummyAccountCreationImport', {
                ...routeParams,
            });
        }
        else {
            navigation.navigateDeprecated('AddBalance', {
                ...routeParams,
            });
        }
    }, [
        route.params,
        clearWebEmojiPickerState,
        params,
        emojiSelected,
        keyBack,
        showNewPhysicalAssets,
        shouldShowCsvImports,
        onAddValuation,
    ]);
    const marginBottom = useMarginBottom('marginBottom', undefined, styles.saveButton);
    return (<AppView type="secondary" isInTab={isWeb} avoidHeader>
      <StatusBar animated barStyle="dark-content"/>
      <View style={styles.container}>
        <View ref={emojiViewRef}>
          <CardView>
            <SettingsItemNavigate title="Emoji" description="Change" icon={emojiSelected ? (<EmojiText fontSize={20}>{emojiSelected}</EmojiText>) : (<IconEmma />)} iconSize={44} titleFirst onPress={onPressEmoji} isSelected={isEmojiPickerOpen} style={styles.containerPadding}/>
          </CardView>
        </View>
      </View>
      {isWeb ? (<WebEmojiPickerWeb {...webEmojiPickerState} onEmojiSelected={onEmojiSelected} defaultSelectedEmoji={emojiSelected || undefined} clearWebEmojiPickerState={clearWebEmojiPickerState}/>) : null}
      <Button brand onPress={saveDetails} style={marginBottom} disabled={!emojiSelected} title="Continue"/>
    </AppView>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    saveButton: {
        marginHorizontal: rem(16),
    },
    containerPadding: {
        ...(isWeb && {
            padding: rem(8),
        }),
    },
});
export default AddAnEmojiScreen;
